/**
 * Forensic fingerprint lookup widget - staff paste a SHA-256 hash
 * (full or 32+ char prefix) extracted from a suspected-leaked
 * workbook, and we tell them who downloaded it, when, and from
 * which deal.
 *
 * Where staff find the hash inside a workbook:
 *   1. File → Info → Properties → Advanced → MedicysFingerprint
 *   2. Hidden white-on-white cell at Costs!R999C100 (full line:
 *      "{hash}|{email}|{timestamp}")
 *   3. Engagement panel → Downloads section → expand a recipient
 *      (when investigating a specific known recipient)
 *
 * Lives on the staff share dashboard, collapsed by default, only
 * needed when investigating a leak - so the affordance is small
 * and unobtrusive.
 */
import { useState } from "react";
import axios from "axios";
import { Search, Loader2, AlertCircle, FileCheck2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDateTime = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};


export default function FingerprintLookup() {
  const [hash, setHash] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const clean = hash.trim().toLowerCase().replace(/[^a-f0-9]/g, "");
    if (clean.length < 16) {
      setError("Hash too short - need at least 16 hex characters.");
      setResult(null);
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await axios.get(
        `${API}/api/proposals/share/fingerprint-lookup`,
        { params: { hash: clean } },
      );
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Lookup failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="fingerprint-lookup"
      className="bg-white border border-stone-200 rounded-2xl p-4 sm:p-5"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="fingerprint-lookup-toggle"
        className="w-full flex items-center justify-between gap-2"
      >
        <span className="flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-[#1B4F8A]" />
          <span className="text-[10px] font-mono tracking-[0.22em] uppercase text-[#1B4F8A]">
            Forensic lookup
          </span>
          <span className="text-[10px] text-stone-400 font-mono">
            · trace a leaked workbook back to the downloader
          </span>
        </span>
        <span className="text-[10px] font-mono uppercase tracking-wider text-stone-400">
          {open ? "Close" : "Open"}
        </span>
      </button>
      {open && (
        <form onSubmit={submit} className="mt-3 space-y-3">
          <div className="space-y-1">
            <label
              htmlFor="fp-hash-input"
              className="block text-[10px] font-mono uppercase tracking-wider text-stone-500"
            >
              SHA-256 hash (or 32+ char prefix)
            </label>
            <p className="text-[10px] text-stone-400">
              Find the hash on the leaked file at <span className="font-mono">File → Info → Properties → Advanced → MedicysFingerprint</span>, or in the hidden cell at <span className="font-mono">Costs!R999C100</span>.
            </p>
            <div className="flex gap-2">
              <input
                id="fp-hash-input"
                data-testid="fingerprint-lookup-input"
                value={hash}
                onChange={(ev) => setHash(ev.target.value)}
                placeholder="5548325d3ce544af8c48004056bc635c..."
                className="flex-1 px-3 py-2 rounded-md border border-stone-300 bg-white text-xs font-mono text-slate-900 focus:outline-none focus:border-[#1B4F8A] focus:ring-1 focus:ring-[#1B4F8A]"
                disabled={busy}
              />
              <button
                type="submit"
                disabled={busy || !hash.trim()}
                data-testid="fingerprint-lookup-submit"
                className="bg-[#1B4F8A] hover:bg-[#163F70] disabled:opacity-40 text-white px-4 py-2 rounded-md text-xs font-medium inline-flex items-center gap-2"
              >
                {busy && <Loader2 className="w-3 h-3 animate-spin" />}
                {busy ? "Looking up…" : "Lookup"}
              </button>
            </div>
          </div>

          {error && (
            <div
              data-testid="fingerprint-lookup-error"
              className="flex items-start gap-2 px-3 py-2 rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-xs"
            >
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {result && result.found && (
            <div
              data-testid="fingerprint-lookup-result"
              className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3"
            >
              <div className="flex items-center gap-2 mb-2">
                <FileCheck2 className="w-4 h-4 text-emerald-700" />
                <p className="text-[10px] font-mono uppercase tracking-wider text-emerald-700">
                  Match found
                </p>
              </div>
              <dl className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1 text-xs text-slate-800">
                <dt className="font-mono text-stone-500 text-[10px] uppercase">CID</dt>
                <dd data-testid="fp-result-cid">#{result.cid}</dd>
                <dt className="font-mono text-stone-500 text-[10px] uppercase">Project</dt>
                <dd className="truncate" data-testid="fp-result-project">{result.project_reference || "-"}</dd>
                <dt className="font-mono text-stone-500 text-[10px] uppercase">Client</dt>
                <dd className="truncate" data-testid="fp-result-client">{result.client_company || "-"}</dd>
                <dt className="font-mono text-stone-500 text-[10px] uppercase">Downloaded by</dt>
                <dd className="font-mono text-rose-700" data-testid="fp-result-email">{result.email || "-"}</dd>
                <dt className="font-mono text-stone-500 text-[10px] uppercase">First at</dt>
                <dd data-testid="fp-result-first">{fmtDateTime(result.first_downloaded_at)}</dd>
                <dt className="font-mono text-stone-500 text-[10px] uppercase">Last at</dt>
                <dd data-testid="fp-result-last">{fmtDateTime(result.last_downloaded_at)}</dd>
                <dt className="font-mono text-stone-500 text-[10px] uppercase">Count</dt>
                <dd data-testid="fp-result-count">{result.download_count}</dd>
                <dt className="font-mono text-stone-500 text-[10px] uppercase">From IP</dt>
                <dd className="font-mono text-[10px] text-stone-500" data-testid="fp-result-ip">{result.ip || "-"}</dd>
                <dt className="font-mono text-stone-500 text-[10px] uppercase">Format</dt>
                <dd className="text-[10px] text-stone-500 font-mono">{result.format}</dd>
              </dl>
            </div>
          )}

          {result && !result.found && (
            <div
              data-testid="fingerprint-lookup-not-found"
              className="flex items-start gap-2 px-3 py-2 rounded-md border border-stone-200 bg-stone-50 text-stone-600 text-xs"
            >
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                No matching download for that hash. Either the file was forged,
                stripped (no MedicysFingerprint property + cell deleted), or
                the hash was mistyped.
              </span>
            </div>
          )}
        </form>
      )}
    </div>
  );
}
